import React, { useState } from "react";
import { Button, Input, Card, Select, message, Space, Typography, Divider } from "antd";
import { DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import axios from "axios";
import { ApiReqBaseUrl } from "../../assets/GlobalVariables";
import { getUserInfo } from "../../services/localStorage";

const { Title, Text } = Typography;
const { Option } = Select;

const OrderPdfDownloader = () => {
  const [orderId, setOrderId] = useState("");
  const [orderType, setOrderType] = useState("package"); // package or hotel
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingDemo, setIsDownloadingDemo] = useState(false);

  const handleDownload = async () => {
    if (!orderId || !orderId.trim()) {
      message.warning("אנא הזן מספר הזמנה");
      return;
    }

    setIsDownloading(true);
    try {
      const trimmedOrderId = orderId.trim();
      
      // Determine the endpoint based on order type
      let url;
      let totalPrice = 0;
      
      if (orderType === "package") {
        // For package orders, try to fetch the order first to get totalPrice
        try {
          const orderRes = await axios.get(`${ApiReqBaseUrl}/lds_orders/${trimmedOrderId}`, {
            withCredentials: true,
            timeout: 10000,
          });
          
          if (orderRes.data && orderRes.data.order) {
            const order = orderRes.data.order;
            // Calculate totalPrice similar to PackageOrderDetailsPremiumView
            const totalToPay = order.totalprice || order.total || 0;
            totalPrice = totalToPay;
            console.log("Fetched totalPrice from order:", totalPrice);
          }
        } catch (fetchError) {
          console.warn("Could not fetch order details, using default totalPrice=0:", fetchError);
          // Continue with default totalPrice=0
        }
        
        url = `${ApiReqBaseUrl}/lds_orders/${trimmedOrderId}/download-pdf?totalPrice=${totalPrice}`;
      } else {
        // For hotel orders
        url = `${ApiReqBaseUrl}/orders/${trimmedOrderId}/download-pdf`;
      }

      console.log("Downloading PDF from:", url);

      const res = await axios.get(url, {
        responseType: "blob",
        withCredentials: true,
        timeout: 60000,
      });

      if (res.status === 200) {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          orderType === "package"
            ? `שובר_חבילה_${trimmedOrderId}.pdf`
            : `אישור_הזמנה_${trimmedOrderId}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success("הקובץ הורד בהצלחה");
      } else {
        message.error("שגיאה בהורדת הקובץ");
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });
      if (error.response?.status === 404) {
        message.error("הזמנה לא נמצאה");
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.error || "שגיאה ביצירת הקובץ";
        message.error(`שגיאה: ${errorMsg}. בדוק את הלוגים בשרת.`);
      } else if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        message.error("הבקשה ארכה זמן רב מדי. נסה שוב.");
      } else if (error.response?.status === 500) {
        message.error("שגיאת שרת. בדוק את הלוגים בשרת.");
      } else {
        message.error(`שגיאה: ${error.message || "לא ניתן להוריד את הקובץ"}`);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadDemo = async () => {
    setIsDownloadingDemo(true);
    try {
      const userInfo = getUserInfo();
      if (!userInfo || !userInfo.id) {
        message.error("לא ניתן לזהות את המשתמש. אנא התחבר מחדש.");
        return;
      }

      const agentId = userInfo.id;
      const url = `${ApiReqBaseUrl}/demo-order-pdf?agent_id=${agentId}`;

      console.log("Downloading demo PDF from:", url);

      const res = await axios.get(url, {
        responseType: "blob",
        withCredentials: true,
        timeout: 60000,
      });

      if (res.status === 200) {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "שובר_דוגמא_DEMO-12345.pdf");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success("הקובץ הורד בהצלחה");
      } else {
        message.error("שגיאה בהורדת הקובץ");
      }
    } catch (error) {
      console.error("Error downloading demo PDF:", error);
      
      // Try to read the error message from the response blob
      let errorMessage = "שגיאה ביצירת הקובץ";
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json.error || json.message || errorMessage;
          console.error("Error from server:", json);
        } catch (e) {
          console.error("Could not parse error response:", e);
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        errorMessage,
      });
      
      if (error.response?.status === 404) {
        message.error(
          "הדף לא נמצא (404). אנא ודא שהשרת רץ עם הקוד החדש והפעל אותו מחדש אם צריך."
        );
      } else if (error.response?.status === 400) {
        message.error(`שגיאה: ${errorMessage}. בדוק את הלוגים בשרת.`);
      } else if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        message.error("הבקשה ארכה זמן רב מדי. נסה שוב.");
      } else if (error.response?.status === 500) {
        message.error("שגיאת שרת. בדוק את הלוגים בשרת.");
      } else {
        message.error(`שגיאה: ${error.message || "לא ניתן להוריד את הקובץ"}`);
      }
    } finally {
      setIsDownloadingDemo(false);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Title level={4} style={{ marginBottom: 16 }}>
        הורדת שובר / אישור הזמנה לבדיקה
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        הזן מספר הזמנה כדי להוריד את השובר או אישור ההזמנה עם פרטי הסוכנות (לוגו + פרטי יצירת קשר)
      </Text>

      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              סוג הזמנה:
            </Text>
            <Select
              value={orderType}
              onChange={setOrderType}
              style={{ width: "100%", maxWidth: 300 }}
            >
              <Option value="package">חבילת נופש</Option>
              <Option value="hotel">מלון</Option>
            </Select>
          </div>

          <div>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              מספר הזמנה:
            </Text>
            <Input
              placeholder="הזן מספר הזמנה (ID או DealID)"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              style={{ maxWidth: 400 }}
              onPressEnter={handleDownload}
            />
          </div>

          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={isDownloading}
            onClick={handleDownload}
            size="large"
            disabled={!orderId || !orderId.trim()}
          >
            הורד {orderType === "package" ? "שובר חבילה" : "אישור הזמנה"}
          </Button>
        </Space>
      </Card>

      <Divider />

      <Card style={{ marginTop: 24, border: "2px dashed #1890ff", backgroundColor: "#f0f7ff" }}>
        <Title level={4} style={{ marginBottom: 16, color: "#1890ff" }}>
          הורדת הזמנה לדוגמא
        </Title>
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          הורד שובר לדוגמא עם מספר הזמנה קבוע (DEMO-12345) ומידע לא אמיתי, אבל עם הלוגו והפרטים שלך
        </Text>
        <Button
          type="primary"
          icon={<FilePdfOutlined />}
          loading={isDownloadingDemo}
          onClick={handleDownloadDemo}
          size="large"
          style={{ backgroundColor: "#1890ff", borderColor: "#1890ff" }}
        >
          הורד שובר לדוגמא (DEMO-12345)
        </Button>
      </Card>

      <Card style={{ marginTop: 24, backgroundColor: "#f5f5f5" }}>
        <Title level={5} style={{ marginBottom: 12 }}>
          הערות:
        </Title>
        <ul style={{ paddingRight: 20, margin: 0 }}>
          <li>
            <Text>
              הקובץ יכלול את פרטי הסוכנות (לוגו + פרטי יצירת קשר) כפי שמוגדרים בפאנל זה
            </Text>
          </li>
          <li>
            <Text>
              עבור חבילות נופש: הקובץ יכלול את כל פרטי החבילה (מלון, טיסות, נוסעים)
            </Text>
          </li>
          <li>
            <Text>
              עבור מלונות: הקובץ יכלול את פרטי המלון והזמנה
            </Text>
          </li>
          <li>
            <Text strong>
              הזמנה לדוגמא: מכילה מידע לא אמיתי (שמות, תאריכים, מספרי טיסות) אבל עם הלוגו והפרטים שלך
            </Text>
          </li>
        </ul>
      </Card>
    </div>
  );
};

export default OrderPdfDownloader;
